import React from 'react';
import Button from 'atoms/Button/Button';
import Stack from 'atoms/Layout/Stack/Stack';
import Typography, { TypographyProps } from 'atoms/Typography/Typography';
import Fade from 'molecules/Fade/Fade';
import BaseFormThankYou from 'molecules/FormHandler/BaseFormThankYou';
import HubspotFormWrapper from 'molecules/HubspotForm/HubspotFormWrapper';
import SpinnerLoading from 'molecules/SpinnerLoading/SpinnerLoading';
import useFreeTrialHubspotFormHandler from 'src/hooks/useFreeTrialHubspotFormHandler';
import { SpinnerWrapper } from './FreeTrialEmailBlock.styles';

export interface FreeTrialEmailBlockProps {
  formWrapperId: string;
  helperTextProps?: Omit<TypographyProps, 'children'>;
  thankYouAlignment?: 'left' | 'center';
  thankYouHeadingProps?: TypographyProps;
  thankYouSupportingTextProps?: TypographyProps;
  buttonText?: string;
  hideHelperText?: boolean;
  extraQueryParams?: Record<string, string>;
}

const FreeTrialEmailBlock = ({
  formWrapperId,
  helperTextProps = {},
  thankYouAlignment = 'center',
  thankYouHeadingProps,
  thankYouSupportingTextProps,
  buttonText = 'Try Mighty For Free',
  hideHelperText = false,
  extraQueryParams,
}: FreeTrialEmailBlockProps): JSX.Element => {
  const { isFormLoading, isLoading, isSubmitted, submitFormAndNavigate } = useFreeTrialHubspotFormHandler({
    formWrapperId,
    extraQueryParams,
  });

  if (isSubmitted) {
    return (
      <BaseFormThankYou
        alignment={thankYouAlignment}
        heading={thankYouHeadingProps}
        supportingText={{
          text: "We can't wait to see what you build.",
          ...thankYouSupportingTextProps,
        }}
      />
    );
  }

  return (
    <Stack
      direction='column'
      align='center'
      position='relative'
      fullWidth
      gap='spacing-lg'
    >
      {isFormLoading && (
        <SpinnerWrapper>
          <SpinnerLoading
            color='var(--brand-900)'
            width={42}
          />
        </SpinnerWrapper>
      )}
      <Fade visible={!isFormLoading} timeout={600}>
        <Stack
          direction='row'
          directionSm='column'
          align='flex-start'
          gap='spacing-xl'
          fullWidth
          maxWidth='width-lg'
        >
          <Stack fullWidth>
            <HubspotFormWrapper
              id={formWrapperId}
              hideInputLabels
              hideLegal
            />
          </Stack>
          <Stack maxWidth='width-fit-content' maxWidthSm='width-xl' fullWidth>
            <Button
              variant='primary'
              size='xl'
              noWrap
              onClick={submitFormAndNavigate}
              disabled={isLoading}
            >
              {buttonText}
            </Button>
          </Stack>
        </Stack>
      </Fade>
      {!hideHelperText && (
        <Typography
          type='paragraph'
          size='sm'
          color='text-tertiary'
          textAlign='center'
          text="Start a free 14-day trial and we'll help you get results. No credit card required."
          {...helperTextProps}
        />
      )}
    </Stack>
  );
};

export default FreeTrialEmailBlock;
